import { useCallback, useRef, useState } from 'react'
import {
  DateTerm,
  DISPLAY_DATE_FORMAT,
  formatDateBy,
  formatDateTime,
} from '../../../../utils/date'
import DateVO from '../../../../vo/DateVO'
import {
  MemberWorkloadStatusGroupRowsByMemberColRowBody,
  MemberWorkloadStatusType,
  MemberWorkloadStatusRow,
  MemberWorkloadStatusSkeleton,
  displayNameComparator,
  fetchMemberWorkloadStatusWorkMonths,
  EstimatedActualType,
  AssignmentType,
  TimeGrain,
} from '../MemberWorkloadStatus'
import { intl } from '../../../../i18n'
import { generateUuid } from '../../../../utils/uuids'
import { UserBasic } from '../../../../lib/functions/user'
import { DivisionProps } from '../../../../lib/functions/division'
import { PositionProps } from '../../../../lib/functions/position'
import { AggregateTarget } from '../../../../domain/entity/WbsItemEntity'

export interface FilterState {
  resourcePlan: boolean
  estimated: boolean
  actual: boolean
}

export const useMemberWorkloadStatusData = () => {
  const rowsGroupingByMember = useRef<MemberWorkloadStatusRow[]>([])
  const originalRowsGroupingByMember = useRef<MemberWorkloadStatusRow[]>([])
  const [rowData, setRowData] = useState<MemberWorkloadStatusRow[]>([])
  const currentFilterState = useRef<FilterState>({
    resourcePlan: true,
    estimated: true,
    actual: true,
  })

  const createRowGroupingByMember = useCallback(
    (source: MemberWorkloadStatusSkeleton[]): MemberWorkloadStatusRow[] => {
      const toMemberWorkMonths = (
        hours:
          | {
              month: string | Date
              manMonth?: number
            }[]
          | undefined
      ) => {
        return (hours ?? []).map(mwm => ({
          yearMonth: formatDateBy(DISPLAY_DATE_FORMAT, mwm.month),
          manMonth: mwm.manMonth,
        }))
      }

      const rows: MemberWorkloadStatusRow[] = []

      const sortedMember = source
        .map(s => s.user)
        .filter(
          (user: UserBasic, index: number, self: UserBasic[]) =>
            self.findIndex(u => u.uuid === user.uuid) === index
        )
        .sort((user1: UserBasic, user2: UserBasic) => {
          const user1Division = source.find(
            s => s.user.uuid === user1.uuid
          )?.division
          const user2Division = source.find(
            s => s.user.uuid === user2.uuid
          )?.division
          const compDivision = displayNameComparator(
            (user1Division as DivisionProps) ?? {},
            (user2Division as DivisionProps) ?? {}
          )
          if (compDivision !== 0) return compDivision

          const user1Position = source.find(
            s => s.user.uuid === user1.uuid
          )?.position
          const user2Position = source.find(
            s => s.user.uuid === user2.uuid
          )?.position
          const compPosition = displayNameComparator(
            (user1Position as PositionProps) ?? {},
            (user2Position as PositionProps) ?? {}
          )
          if (compPosition !== 0) return compPosition
          return user1.code.localeCompare(user2.code)
        })

      sortedMember.forEach((member: UserBasic) => {
        const targetSource = source.filter(s => s.user.uuid === member.uuid)

        const memberRow: MemberWorkloadStatusRow = {
          uuid: member.uuid,
          type: MemberWorkloadStatusType.MEMBER,
          estimatedActualType: EstimatedActualType.RESOURCE_PLAN,
          body: {
            user: member,
            division: targetSource[0].division,
            position: targetSource[0].position,
            project: {
              code: '',
              iconUrl: '',
              name: intl.formatMessage({ id: 'project.resourcePlan.total' }),
              uuid: generateUuid(),
            },
            role: targetSource[0].role,
            validFrom: formatDateTime(targetSource[0].validFrom),
            validTo: formatDateTime(targetSource[0].validTo),
            validUser: targetSource[0].validUser,
            memberWorkMonths: [],
          } as MemberWorkloadStatusGroupRowsByMemberColRowBody,
          treeValue: [member.uuid],
          isParentRow: true,
        }
        const totalEstimatedRowUuid = `${member.uuid}:total:estimated`
        const totalEstimatedRow: MemberWorkloadStatusRow = {
          uuid: totalEstimatedRowUuid,
          type: MemberWorkloadStatusType.TOTAL_ESTIMATED_ACTUAL,
          estimatedActualType: EstimatedActualType.ESTIMATED,
          body: {
            ...memberRow.body,
            memberWorkMonths: [],
          },
          treeValue: [member.uuid, totalEstimatedRowUuid],
        }
        const totalActualRowUuid = `${member.uuid}:total:actual`
        const totalActualRow: MemberWorkloadStatusRow = {
          uuid: totalActualRowUuid,
          type: MemberWorkloadStatusType.TOTAL_ESTIMATED_ACTUAL,
          estimatedActualType: EstimatedActualType.ACTUAL,
          body: {
            ...memberRow.body,
            memberWorkMonths: [],
          },
          treeValue: [member.uuid, totalActualRowUuid],
        }
        rows.push(memberRow, totalEstimatedRow, totalActualRow)

        const projectWorkloads = targetSource.flatMap(
          s => s.workloadStatus ?? []
        )
        projectWorkloads.forEach(projectWorkload => {
          const projectUuid = projectWorkload.project.uuid
          const projectRow: MemberWorkloadStatusRow = {
            uuid: `${member.uuid}:project:${projectUuid}`,
            type: MemberWorkloadStatusType.PROJECT,
            estimatedActualType: EstimatedActualType.RESOURCE_PLAN,
            body: {
              ...memberRow.body,
              user: member,
              project: {
                code: projectWorkload.project.code,
                iconUrl: projectWorkload.project.iconUrl,
                name: projectWorkload.project.displayName,
                uuid: projectWorkload.project.uuid,
              },
              memberWorkMonths: toMemberWorkMonths(
                projectWorkload.resourcePlan
              ),
            },
            revision: projectWorkload.project.revision,
            lockVersion: projectWorkload.project.lockVersion,
            createdAt: projectWorkload.project.createdAt,
            createdBy: projectWorkload.project.createdBy,
            updatedAt: projectWorkload.project.updatedAt,
            updatedBy: projectWorkload.project.updatedBy,
            treeValue: [
              memberRow.uuid,
              `${member.uuid}:project:${projectUuid}`,
            ],
            isParentRow: true,
          }
          const projectEstimatedRowUuid = `${member.uuid}:project:${projectUuid}:estimated`
          const projectEstimatedRow: MemberWorkloadStatusRow = {
            uuid: projectEstimatedRowUuid,
            type: MemberWorkloadStatusType.ESTIMATED_ACTUAL,
            estimatedActualType: EstimatedActualType.ESTIMATED,
            body: {
              ...projectRow.body,
              memberWorkMonths: toMemberWorkMonths(
                projectWorkload.estimatedHours
              ),
            },
            treeValue: [
              memberRow.uuid,
              `${member.uuid}:project:${projectUuid}`,
              projectEstimatedRowUuid,
            ],
          }
          const projectActualRowUuid = `${member.uuid}:project:${projectUuid}:actual`
          const projectActualRow: MemberWorkloadStatusRow = {
            uuid: projectActualRowUuid,
            type: MemberWorkloadStatusType.ESTIMATED_ACTUAL,
            estimatedActualType: EstimatedActualType.ACTUAL,
            body: {
              ...projectRow.body,
              memberWorkMonths: toMemberWorkMonths(projectWorkload.actualHours),
            },
            treeValue: [
              memberRow.uuid,
              `${member.uuid}:project:${projectUuid}`,
              projectActualRowUuid,
            ],
          }
          rows.push(projectRow, projectEstimatedRow, projectActualRow)
        })
      })

      return rows
    },
    []
  )

  const reconstructRowHierarchy = useCallback(
    (
      rows: MemberWorkloadStatusRow[],
      filterState: FilterState
    ): MemberWorkloadStatusRow[] => {
      const { resourcePlan, estimated, actual } = filterState

      if (resourcePlan && estimated && actual) {
        return rows
      }

      const reconstructedRows: MemberWorkloadStatusRow[] = []

      const memberUuids = Array.from(
        new Set(
          rows
            .filter(row => row.type === MemberWorkloadStatusType.MEMBER)
            .map(row => row.uuid)
        )
      )

      memberUuids.forEach(memberUuid => {
        const memberRow = rows.find(
          row =>
            row.type === MemberWorkloadStatusType.MEMBER &&
            row.uuid === memberUuid
        )
        if (!memberRow) return

        const totalEstimatedRow = rows.find(
          row =>
            row.type === MemberWorkloadStatusType.TOTAL_ESTIMATED_ACTUAL &&
            row.estimatedActualType === EstimatedActualType.ESTIMATED &&
            row.uuid === `${memberUuid}:total:estimated`
        )
        const totalActualRow = rows.find(
          row =>
            row.type === MemberWorkloadStatusType.TOTAL_ESTIMATED_ACTUAL &&
            row.estimatedActualType === EstimatedActualType.ACTUAL &&
            row.uuid === `${memberUuid}:total:actual`
        )
        const projectRows = rows.filter(
          row =>
            row.type === MemberWorkloadStatusType.PROJECT &&
            row.uuid.startsWith(`${memberUuid}:project:`)
        )

        if (!resourcePlan) {
          if (estimated && actual) {
            if (totalEstimatedRow) {
              reconstructedRows.push({
                ...totalEstimatedRow,
                treeValue: [totalEstimatedRow.uuid],
                isRootParentRow: true,
                isParentRow: true,
              })
            }
            if (totalActualRow) {
              reconstructedRows.push({
                ...totalActualRow,
                treeValue: [totalEstimatedRow!.uuid, totalActualRow.uuid],
              })
            }
            projectRows.forEach(projectRow => {
              const projectUuid = projectRow.uuid.split(':')[2]
              const projectEstimatedRow = rows.find(
                row =>
                  row.type === MemberWorkloadStatusType.ESTIMATED_ACTUAL &&
                  row.estimatedActualType === EstimatedActualType.ESTIMATED &&
                  row.uuid === `${memberUuid}:project:${projectUuid}:estimated`
              )
              const projectActualRow = rows.find(
                row =>
                  row.type === MemberWorkloadStatusType.ESTIMATED_ACTUAL &&
                  row.estimatedActualType === EstimatedActualType.ACTUAL &&
                  row.uuid === `${memberUuid}:project:${projectUuid}:actual`
              )
              if (projectEstimatedRow) {
                reconstructedRows.push({
                  ...projectEstimatedRow,
                  treeValue: [
                    totalEstimatedRow!.uuid,
                    projectEstimatedRow.uuid,
                  ],
                  isParentRow: true,
                })
              }
              if (projectActualRow && projectEstimatedRow) {
                reconstructedRows.push({
                  ...projectActualRow,
                  treeValue: [
                    totalEstimatedRow!.uuid,
                    projectEstimatedRow.uuid,
                    projectActualRow.uuid,
                  ],
                })
              }
            })
          } else if (estimated && !actual) {
            if (totalEstimatedRow) {
              reconstructedRows.push({
                ...totalEstimatedRow,
                treeValue: [totalEstimatedRow.uuid],
                isRootParentRow: true,
                isParentRow: true,
              })
            }
            projectRows.forEach(projectRow => {
              const projectUuid = projectRow.uuid.split(':')[2]
              const projectEstimatedRow = rows.find(
                row =>
                  row.type === MemberWorkloadStatusType.ESTIMATED_ACTUAL &&
                  row.estimatedActualType === EstimatedActualType.ESTIMATED &&
                  row.uuid === `${memberUuid}:project:${projectUuid}:estimated`
              )
              if (projectEstimatedRow) {
                reconstructedRows.push({
                  ...projectEstimatedRow,
                  treeValue: [
                    totalEstimatedRow!.uuid,
                    projectEstimatedRow.uuid,
                  ],
                  isParentRow: true,
                })
              }
            })
          } else if (!estimated && actual) {
            if (totalActualRow) {
              reconstructedRows.push({
                ...totalActualRow,
                treeValue: [totalActualRow.uuid],
                isRootParentRow: true,
                isParentRow: true,
              })
            }
            projectRows.forEach(projectRow => {
              const projectUuid = projectRow.uuid.split(':')[2]
              const projectActualRow = rows.find(
                row =>
                  row.type === MemberWorkloadStatusType.ESTIMATED_ACTUAL &&
                  row.estimatedActualType === EstimatedActualType.ACTUAL &&
                  row.uuid === `${memberUuid}:project:${projectUuid}:actual`
              )
              if (projectActualRow) {
                reconstructedRows.push({
                  ...projectActualRow,
                  treeValue: [totalActualRow!.uuid, projectActualRow.uuid],
                  isParentRow: true,
                })
              }
            })
          }
        } else {
          reconstructedRows.push(memberRow)
          reconstructedRows[reconstructedRows.length - 1].isParentRow = true
          if (estimated && totalEstimatedRow) {
            reconstructedRows.push(totalEstimatedRow)
          }
          if (actual && totalActualRow) {
            reconstructedRows.push(totalActualRow)
          }
          projectRows.forEach(projectRow => {
            const projectUuid = projectRow.uuid.split(':')[2]
            reconstructedRows.push(projectRow)
            if (estimated) {
              const projectEstimatedRow = rows.find(
                row =>
                  row.type === MemberWorkloadStatusType.ESTIMATED_ACTUAL &&
                  row.estimatedActualType === EstimatedActualType.ESTIMATED &&
                  row.uuid === `${memberUuid}:project:${projectUuid}:estimated`
              )
              if (projectEstimatedRow) {
                reconstructedRows.push(projectEstimatedRow)
              }
            }
            if (actual) {
              const projectActualRow = rows.find(
                row =>
                  row.type === MemberWorkloadStatusType.ESTIMATED_ACTUAL &&
                  row.estimatedActualType === EstimatedActualType.ACTUAL &&
                  row.uuid === `${memberUuid}:project:${projectUuid}:actual`
              )
              if (projectActualRow) {
                reconstructedRows.push(projectActualRow)
              }
            }
          })
        }
      })

      return reconstructedRows
    },
    []
  )

  const switchRowDataByGroupColumn = useCallback(() => {
    setRowData(rowsGroupingByMember.current)
  }, [])

  const applyFilter = useCallback(
    (filterState: FilterState) => {
      currentFilterState.current = filterState
      const reconstructed = reconstructRowHierarchy(
        originalRowsGroupingByMember.current,
        filterState
      )
      rowsGroupingByMember.current = reconstructed
      setRowData(reconstructed)
    },
    [reconstructRowHierarchy]
  )

  const fetchRecords = useCallback(
    async (
      dateTerm: DateTerm,
      aggregateTarget: AggregateTarget,
      timeGrain: TimeGrain,
      assignmentType: AssignmentType
    ) => {
      if (!dateTerm || !dateTerm.startDate || !dateTerm.endDate) {
        return
      }
      const startDate = new DateVO(dateTerm.startDate).formatForApi()
      const endDate = new DateVO(dateTerm.endDate).formatForApi()
      if (!startDate || !endDate) {
        return
      }
      const response: MemberWorkloadStatusSkeleton[] =
        await fetchMemberWorkloadStatusWorkMonths({
          startDate,
          endDate,
          aggregateTarget,
          timeGrain,
          assignmentType,
        })
      const createdRows = createRowGroupingByMember(response)
      originalRowsGroupingByMember.current = createdRows
      rowsGroupingByMember.current = createdRows
      applyFilter(currentFilterState.current)
    },
    [createRowGroupingByMember, applyFilter]
  )
  return {
    rowData,
    fetchRecords,
    switchRowDataByGroupColumn,
    applyFilter,
  }
}
